import { PageIcon } from "@/components/PageIcon";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import ShiftForm from "@/components/scheduler/ShiftForm";
import { formatDateTime } from "@/lib/datetime";
import { RotateCcw, CalendarClock, AlertTriangle, CalendarDays, Loader2, CalendarPlus } from "lucide-react";

// Start-of-day / start-of-week (Mon) helpers in local time.
function dayBounds() {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dow = (startOfToday.getDay() + 6) % 7; // 0 = Monday
  const startOfWeek = new Date(startOfToday);
  startOfWeek.setDate(startOfToday.getDate() - dow);
  return { now, startOfToday, startOfWeek };
}

function rebookDate(row) {
  const r = row.rebook || {};
  const raw = r.server_at || r.at || row.updated_at;
  const d = raw ? new Date(raw) : null;
  return d && !isNaN(d.getTime()) ? d : null;
}

const BUCKETS = [
  { key: "today", label: "Today", icon: CalendarDays, tone: "indigo" },
  { key: "this_week", label: "This Week", icon: CalendarClock, tone: "amber" },
  { key: "overdue", label: "Overdue", icon: AlertTriangle, tone: "rose" },
];

const TONE = {
  indigo: { card: "border-indigo-200 bg-indigo-50", active: "ring-2 ring-indigo-400", icon: "text-indigo-600", num: "text-indigo-700" },
  amber: { card: "border-amber-200 bg-amber-50", active: "ring-2 ring-amber-400", icon: "text-amber-600", num: "text-amber-700" },
  rose: { card: "border-rose-200 bg-rose-50", active: "ring-2 ring-rose-400", icon: "text-rose-600", num: "text-rose-700" },
};

export default function RebookDashboard() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeBucket, setActiveBucket] = useState(null);
  const [reschedule, setReschedule] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      let all = [];
      let page = 1;
      for (;;) {
        const r = await api.get("/tasks", { params: { job_summary_status: "REBOOK", page, page_size: 100 } });
        all = all.concat(r.data?.items || []);
        if (page >= (r.data?.total_pages || 1) || page > 10) break;
        page += 1;
      }
      setRows(all);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const enriched = useMemo(() => {
    const { now, startOfToday, startOfWeek } = dayBounds();
    return rows.map((row) => {
      const d = rebookDate(row);
      let bucket = "overdue";
      let days = 0;
      if (d) {
        if (d >= startOfToday) bucket = "today";
        else if (d >= startOfWeek) bucket = "this_week";
        else bucket = "overdue";
        days = Math.max(0, Math.floor((now - d) / 86400000));
      }
      return { ...row, _bucket: bucket, _days: days };
    });
  }, [rows]);

  const counts = useMemo(() => {
    const c = { today: 0, this_week: 0, overdue: 0 };
    enriched.forEach((r) => { c[r._bucket] += 1; });
    return c;
  }, [enriched]);

  const visible = useMemo(
    () => (activeBucket ? enriched.filter((r) => r._bucket === activeBucket) : enriched),
    [enriched, activeBucket],
  );

  return (
    <div className="px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8" data-testid="page-rebook-dashboard">
      <div className="flex items-center gap-3 mb-4">
        <PageIcon />
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Rebook Jobs</h1>
          <p className="text-sm text-slate-500 mt-0.5">Jobs the field flagged for rescheduling, by how long they've been waiting.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        {BUCKETS.map((b) => {
          const t = TONE[b.tone];
          const isActive = activeBucket === b.key;
          const Icon = b.icon;
          return (
            <button
              key={b.key}
              onClick={() => setActiveBucket((cur) => (cur === b.key ? null : b.key))}
              data-testid={`rebook-bucket-${b.key}`}
              className={`text-left rounded-2xl border p-4 transition-all hover:shadow-sm ${t.card} ${isActive ? t.active : ""}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-600">{b.label}</span>
                <Icon className={`w-4 h-4 ${t.icon}`} />
              </div>
              <div className={`mt-2 text-3xl font-bold tabular-nums ${t.num}`} data-testid={`rebook-bucket-count-${b.key}`}>
                {loading ? "—" : counts[b.key]}
              </div>
            </button>
          );
        })}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100">
          <div className="text-xs text-slate-500">
            {loading
              ? <span className="inline-flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading rebook jobs…</span>
              : <><span className="font-semibold text-slate-700">{visible.length}</span> job{visible.length === 1 ? "" : "s"}{activeBucket ? ` · ${BUCKETS.find((b) => b.key === activeBucket)?.label}` : " · all"}</>}
          </div>
          {activeBucket && (
            <button onClick={() => setActiveBucket(null)} className="text-xs text-slate-500 hover:text-indigo-600 hover:underline" data-testid="rebook-clear-filter">
              Clear filter
            </button>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse" data-testid="rebook-table">
            <thead>
              <tr className="bg-slate-50 text-left text-xs font-medium text-slate-500 border-b border-slate-200">
                <th className="px-6 py-3.5 whitespace-nowrap">Job #</th>
                <th className="px-6 py-3.5 whitespace-nowrap">Customer</th>
                <th className="px-6 py-3.5">Reason</th>
                <th className="px-6 py-3.5 whitespace-nowrap">Engineer</th>
                <th className="px-6 py-3.5 whitespace-nowrap">Original Appointment</th>
                <th className="px-6 py-3.5 whitespace-nowrap">Postcode</th>
                <th className="px-6 py-3.5 whitespace-nowrap text-right">Days Waiting</th>
                <th className="px-6 py-3.5 whitespace-nowrap text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {!loading && visible.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-16 text-center text-slate-400 text-sm">
                  No rebook jobs{activeBucket ? ` in “${BUCKETS.find((b) => b.key === activeBucket)?.label}”` : ""}. Nothing to reschedule right now.
                </td></tr>
              )}
              {visible.map((r) => (
                <tr key={r.legacy_id} className="border-b border-slate-100 hover:bg-slate-50/70 transition-colors" data-testid={`rebook-row-${r.legacy_id}`}>
                  <td className="px-6 py-4 text-sm">
                    <button onClick={() => navigate(`/tasks/${r.legacy_id}`)} className="mono text-xs font-semibold text-indigo-600 hover:underline" data-testid={`rebook-jobnum-${r.legacy_id}`}>
                      {r.legacy_id}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-700 max-w-[200px] truncate" title={r.public_contact || ""}>{r.public_contact || <span className="text-slate-300">—</span>}</td>
                  <td className="px-6 py-4 text-sm text-slate-600 max-w-[280px] truncate" title={r.rebook?.reason || ""}>{r.rebook?.reason || <span className="text-slate-300">—</span>}</td>
                  <td className="px-6 py-4 text-sm text-slate-600 whitespace-nowrap">{r.rebook?.by_name || <span className="text-slate-300">—</span>}</td>
                  <td className="px-6 py-4 text-sm text-slate-600 whitespace-nowrap">{r.original_appointment || <span className="text-slate-300">—</span>}</td>
                  <td className="px-6 py-4 text-sm text-slate-500 mono text-xs whitespace-nowrap">{r.venue_postcode || <span className="text-slate-300">—</span>}</td>
                  <td className="px-6 py-4 text-sm text-right whitespace-nowrap">
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold tabular-nums ${r._bucket === "overdue" ? "border-rose-200 bg-rose-50 text-rose-700" : r._bucket === "this_week" ? "border-amber-200 bg-amber-50 text-amber-700" : "border-indigo-200 bg-indigo-50 text-indigo-700"}`}>
                      {r._days}d
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" className="h-8 gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => setReschedule(r)} data-testid={`rebook-reschedule-${r.legacy_id}`}>
                      <CalendarPlus className="w-3.5 h-3.5" /> Reschedule
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {reschedule && (
        <ShiftForm
          initial={{ task_legacy_id: reschedule.legacy_id }}
          onClose={() => setReschedule(null)}
          onSaved={() => { setReschedule(null); load(); }}
        />
      )}
    </div>
  );
}
